import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { RootState } from "../../app/store";
import { buildConsumedMealFoodstuffs } from "../../common/consumedMealPayload";
import { calculateNutritionFromFoodstuffs } from "../../common/mealMetrics";
import { uiTokens } from "../../common/uiTokens";
import type { ConsumedMeal } from "../../models/ConsumedMeal";
import type { Foodstuff } from "../../models/Foodstuff";
import type { RecommendedMeal } from "../../models/RecommendedMeal";
import { Gauges } from "../common/Gauges";
import { RecommendedMealCard } from "../meal/RecommendedMealCard";
import RecordMeal from "../meal/RecordMeal";
import {
    getConsumedMeals,
    recommendedMealsForUser,
    deleteConsumedMeal,
    saveConsumedMeal,
    setConsumedMeal
} from "../../state/meals/mealSlice";
import { getUser } from "../../state/user/userSlice";

const resolveTime = (consumptionTime?: string): string => {
    if (!consumptionTime) return '--:--';

    const parsed = new Date(consumptionTime);
    if (Number.isNaN(parsed.getTime())) {
        return '--:--';
    }

    return new Intl.DateTimeFormat('cs-CZ', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed);
}



const getDayKey = (value?: unknown): string => {
    const keys = getDayKeys(value);
    return keys[0] || '';
}

const getDayKeys = (value?: unknown): string[] => {
    if (!value) return [];

    const asString = (
        typeof value === 'string'
            ? value
            : value instanceof Date
                ? value.toISOString()
                : String(value)
    ).trim();

    const keys = new Set<string>();
    const parsed = new Date(asString);

    if (!Number.isNaN(parsed.getTime())) {
        const localDate = new Intl.DateTimeFormat('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(parsed);
        const utcDate = parsed.toISOString().slice(0, 10);
        keys.add(localDate);
        keys.add(utcDate);
    }

    const isoDateMatch = asString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) {
        keys.add(isoDateMatch[1]);
        return Array.from(keys);
    }

    const fallbackDate = asString.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(fallbackDate)) {
        keys.add(fallbackDate);
    }

    return Array.from(keys);
}

const isSameDayFor = (value: unknown, dayKey: string): boolean => {
    return getDayKeys(value).includes(dayKey);
};

const resolveConsumedMealTitle = (meal: ConsumedMeal): string => {
    return meal.name || meal.meal?.name || 'Nezadaný název jídla';
}

const resolveConsumedMealKey = (meal: ConsumedMeal, index: number): string => {
    return meal.id || `consumed-${index}-${meal.consumptionTime ?? 'time'}-${meal.meal?.id || meal.name || 'meal'}`;
}

export function ClientDashboard() {
    const dispatch = useDispatch<any>()
    const user = useSelector((state: RootState) => state.user.user);
    const recommendedMeals = useSelector((state: RootState) => state.meal.recommendedMeals);
    const consumedMeals = useSelector((state: RootState) => state.meal.consumedMeals);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { hash } = useLocation();

    const { t } = useTranslation()
    const [toastOpen, setToastOpen] = useState(false);
    const [errorToastOpen, setErrorToastOpen] = useState(false);
    const [loggingMeals, setLoggingMeals] = useState<Record<string, boolean>>({});
    const [deletedMeals, setDeletedMeals] = useState<Record<string, boolean>>({});
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);

    useEffect(() => {
        dispatch(getUser());
        dispatch(getConsumedMeals());
    }, [dispatch]);

    useEffect(() => {
        if (user.id) {
            dispatch(recommendedMealsForUser(user.id))
        }
    }, [dispatch, user.id]);

    useEffect(() => {
        if (hash === '#log') {
            document.getElementById('log')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [hash]);

    const todayKey = useMemo(() => getDayKey(new Date()), []);
    const loggedLookup = useMemo(() => {
        const lookup: Record<string, boolean> = {};
        consumedMeals?.forEach((meal) => {
            const mealId = meal.meal?.id;
            if (!mealId) return;
            if (isSameDayFor(meal.consumptionTime, todayKey)) {
                lookup[mealId] = true;
            }
        });
        return lookup;
    }, [consumedMeals, todayKey]);

    const todayMeals = useMemo(() => {
        const meals = consumedMeals
            ?.map((meal, index) => ({
                meal,
                key: resolveConsumedMealKey(meal, index),
                isToday: !meal.consumptionTime || isSameDayFor(meal.consumptionTime, todayKey),
            }))
            .filter((entry) => !deletedMeals[entry.key]) || [];

        meals.sort((a, b) => {
            const aTime = a.meal.consumptionTime ? new Date(a.meal.consumptionTime).getTime() : 0;
            const bTime = b.meal.consumptionTime ? new Date(b.meal.consumptionTime).getTime() : 0;
            return bTime - aTime;
        });
        console.log(consumedMeals, meals)
        return meals;
    }, [consumedMeals, todayKey, deletedMeals]);

    const hasLoggedMeals = todayMeals.length > 0;

    const guessedMealtime = () => {
        const hour = new Date().getHours();
        if (hour < 10) return 'BREAKFAST';
        if (hour < 12) return 'SNACK_AM';
        if (hour < 15) return 'LUNCH';
        if (hour < 18) return 'SNACK_PM';
        return 'DINNER';
    };

    const hasAvailableRecommendations = useMemo(() => {
        return recommendedMeals.some((recommendation) => {
            const mealId = recommendation.meal?.id;
            if (!mealId) return true;
            return !loggedLookup[mealId] && !loggingMeals[mealId];
        });
    }, [recommendedMeals, loggedLookup, loggingMeals]);

    const handleLogRecommendation = (recommendation: RecommendedMeal, editedFoodstuffs?: Foodstuff[]) => {
        const mealId = recommendation.meal.id;
        if (!mealId) return;
        if (loggedLookup[mealId] || loggingMeals[mealId]) return;

        setLoggingMeals((prev) => ({ ...prev, [mealId]: true }));
        const payload = {
            meal: {
                id: mealId,
                languageCode: recommendation.meal.languageCode,
            },
            name: recommendation.meal.name,
            mealtime: recommendation.meal.mealtime || guessedMealtime(),
            consumptionTime: new Date().toISOString(),
            foodstuffs: buildConsumedMealFoodstuffs((editedFoodstuffs && editedFoodstuffs.length > 0) ? editedFoodstuffs : (recommendation.meal.foodstuffs || [])),
        };

        dispatch(saveConsumedMeal(payload))
            .unwrap()
            .then(() => {
                setToastOpen(true);
            })
            .catch(() => {
                setErrorToastOpen(true);
            })
            .finally(() => {
                setLoggingMeals((prev) => {
                    const next = { ...prev };
                    delete next[mealId];
                    return next;
                });
            });
    };

    const handleToastClose = () => {
        setToastOpen(false);
    };

    const handleErrorToastClose = () => {
        setErrorToastOpen(false);
    };

    const handleRecordFirstMealClick = () => {
        document.getElementById('log')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (window.location.hash !== '#log') {
            window.history.replaceState(null, '', '#log');
        }
    };

    const handleEditMeal = (meal: ConsumedMeal) => {
        dispatch(setConsumedMeal({
            ...meal,
            mealtime: meal.mealtime || guessedMealtime(),
            consumptionTime: meal.consumptionTime || new Date().toISOString().slice(0, 16),
            foodstuffs: (meal.foodstuffs || meal.meal?.foodstuffs || []).map((item) => ({ ...item })),
            meal: meal.meal ? { ...meal.meal } : undefined,
        }));
        handleRecordFirstMealClick();
    };

    const handleRepeatMeal = (meal: ConsumedMeal) => {
        dispatch(setConsumedMeal({
            ...meal,
            id: undefined,
            mealtime: meal.mealtime || guessedMealtime(),
            consumptionTime: new Date().toISOString().slice(0, 16),
            foodstuffs: (meal.foodstuffs || meal.meal?.foodstuffs || []).map((item) => ({ ...item })),
            meal: meal.meal ? { ...meal.meal } : undefined,
        }));
        handleRecordFirstMealClick();
    };

    const handleDeleteMeal = (meal: ConsumedMeal, key: string) => {
        if (!meal.id) {
            setDeletedMeals((prev) => ({
                ...prev,
                [key]: true,
            }));
            return;
        }

        dispatch(deleteConsumedMeal(meal.id))
            .unwrap()
            .then(() => {
                setDeletedMeals((prev) => ({
                    ...prev,
                    [key]: true,
                }));
            })
            .catch(() => {
                setErrorToastOpen(true);
            });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: 1240,
                mx: 'auto',
                p: { xs: 1.5, md: 3 },
                pb: { xs: 2, md: 3 },
                gap: { xs: 2, md: 3 },
            }}
        >
            <Box
                sx={{
                    backgroundColor: uiTokens.colors.surface,
                    border: `1px solid ${uiTokens.colors.border}`,
                    borderRadius: uiTokens.radius.card,
                    boxShadow: uiTokens.shadow.soft,
                    p: { xs: 2, md: 3 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: uiTokens.colors.textPrimary }}>
                            {t('client_dashboard.today_progress')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary, mt: 0.75 }}>
                            {t('client_dashboard.today_progress_helper')}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleRecordFirstMealClick}
                        startIcon={<RestaurantIcon />}
                        sx={{
                            alignSelf: { xs: 'stretch', md: 'center' },
                            borderRadius: 999,
                            backgroundColor: uiTokens.colors.primary,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            px: 2.5,
                            '&:hover': { backgroundColor: uiTokens.colors.primaryDark }
                        }}
                    >
                        + Zaznamenat vlastní jídlo
                    </Button>
                </Box>
                <Gauges />
            </Box>

            <Box
                sx={{
                    backgroundColor: uiTokens.colors.surfaceMuted,
                    borderRadius: uiTokens.radius.card,
                    border: `1px solid ${uiTokens.colors.border}`,
                    p: { xs: 2, md: 3 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ mb: 0.75, fontWeight: 700, color: uiTokens.colors.textPrimary }}>
                            {t('client_dashboard.what_to_eat_next')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary }}>
                            {t('client_dashboard.recommended_meals_rationale')}
                        </Typography>
                    </Box>
                    <Button
                        variant="text"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHowItWorksOpen(true)}
                        sx={{
                            color: uiTokens.colors.primary,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 0,
                        }}
                    >
                        {t('client_dashboard.how_it_works')}
                    </Button>
                </Box>

                {hasAvailableRecommendations ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 3,
                        }}
                    >
                        {recommendedMeals.length > 0 && (
                            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                                <RecommendedMealCard
                                    recommendation={recommendedMeals[0]}
                                    variant="primary"
                                    onLogNow={handleLogRecommendation}
                                    isLogged={Boolean(recommendedMeals[0]?.meal?.id && loggedLookup[recommendedMeals[0].meal.id])}
                                    isLogging={Boolean(recommendedMeals[0]?.meal?.id && loggingMeals[recommendedMeals[0].meal.id])}
                                />
                            </Box>
                        )}
                        {recommendedMeals.slice(1, 5).map((recommendation, index) => (
                            <RecommendedMealCard
                                key={`${recommendation.meal.id ?? index}`}
                                recommendation={recommendation}
                                variant="secondary"
                                onLogNow={handleLogRecommendation}
                                isLogged={Boolean(recommendation.meal?.id && loggedLookup[recommendation.meal.id])}
                                isLogging={Boolean(recommendation.meal?.id && loggingMeals[recommendation.meal.id])}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{ color: uiTokens.colors.textPrimary, p: 1 }}>
                        Dnešní plán je hotový. Níže najdete přehled jídel, která jste dnes zaznamenali.
                    </Typography>
                )}

                <Box
                    sx={{
                        mt: 2.5,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: uiTokens.colors.surface,
                        border: `1px solid ${uiTokens.colors.border}`,
                    }}
                >
                    <Typography variant="body2" sx={{ color: uiTokens.colors.textPrimary, fontWeight: 600 }}>
                        {t('client_dashboard.adjustment_box_title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary, mt: 0.75 }}>
                        {t('client_dashboard.adjustment_box_body')}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    backgroundColor: uiTokens.colors.surface,
                    border: `1px solid ${uiTokens.colors.border}`,
                    borderRadius: uiTokens.radius.card,
                    boxShadow: uiTokens.shadow.soft,
                    p: { xs: 2, md: 3 },
                }}
            >
                <Typography variant="h5" sx={{ mb: 0.75, fontWeight: 700, color: uiTokens.colors.textPrimary }}>
                    Dnešní jídla
                </Typography>

                {todayMeals.length === 0 ? (
                    <Box
                        sx={{
                            borderRadius: 2,
                            border: `1px dashed ${uiTokens.colors.border}`,
                            p: 2,
                            backgroundColor: uiTokens.colors.surfaceMuted,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary }}>
                            Ještě jste dnes nezaznamenali žádná jídla. Použijte tlačítko „+ Zaznamenat vlastní jídlo“ nebo doporučené varianty nahoře.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {todayMeals.map(({ meal, key }) => {
                            const kcal = calculateNutritionFromFoodstuffs(meal.foodstuffs || meal.meal?.foodstuffs || [])?.energyKcal || 0;
                            return (
                                <Box
                                    key={key}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '110px 1fr', sm: '110px 1fr auto' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 1.5,
                                        p: 1.75,
                                        borderRadius: 2,
                                        backgroundColor: '#FFF',
                                        border: `1px solid ${uiTokens.colors.border}`,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#8A7F74',
                                            fontWeight: 700,
                                            letterSpacing: 0.15,
                                        }}
                                    >
                                        {resolveTime(meal.consumptionTime)}
                                    </Typography>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: uiTokens.colors.textPrimary, fontWeight: 700 }}>
                                            {resolveConsumedMealTitle(meal)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary }}>
                                            {kcal.toFixed(0)} kcal
                                        </Typography>
                                    </Box>

                                    <Stack spacing={1} direction="row" sx={{ justifySelf: { xs: 'stretch', sm: 'flex-end' }, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            onClick={() => handleEditMeal(meal)}
                                            sx={{ textTransform: 'none', color: uiTokens.colors.primary, borderRadius: 999 }}
                                        >
                                            Upravit
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<DeleteOutlineIcon />}
                                            onClick={() => handleDeleteMeal(meal, key)}
                                            sx={{ textTransform: 'none', color: '#B35A4A', borderRadius: 999 }}
                                        >
                                            Smazat
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<HistoryIcon />}
                                            onClick={() => handleRepeatMeal(meal)}
                                            sx={{ textTransform: 'none', color: uiTokens.colors.textPrimary, borderRadius: 999 }}
                                        >
                                            Zopakovat / použít znovu
                                        </Button>
                                    </Stack>
                                </Box>
                            )
                        })}
                    </Stack>
                )}
            </Box>

            <Box
                id="log"
                sx={{
                    backgroundColor: uiTokens.colors.surface,
                    border: `1px solid ${uiTokens.colors.border}`,
                    borderRadius: uiTokens.radius.card,
                    boxShadow: uiTokens.shadow.soft,
                    p: { xs: 2, md: 3 },
                }}
            >
                <Typography variant="h5" sx={{ mb: 0.75, fontWeight: 700, color: uiTokens.colors.textPrimary }}>
                    Zaznamenat vlastní jídlo
                </Typography>
                {(isMobile || hasLoggedMeals) && (
                    <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary, mb: 2 }}>
                        {t('client_dashboard.record_meal_helper')}
                    </Typography>
                )}
                <RecordMeal />
            </Box>

            <Snackbar
                open={toastOpen}
                onClose={handleToastClose}
                autoHideDuration={6000}
                message={t('recommended_meal.logged_toast')}
            />
            <Snackbar
                open={errorToastOpen}
                onClose={handleErrorToastClose}
                autoHideDuration={6000}
            >
                <Alert onClose={handleErrorToastClose} severity="error" sx={{ width: '100%' }}>
                    {t('recommended_meal.logged_failed')}
                </Alert>
            </Snackbar>

            <Dialog open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('client_dashboard.how_it_works')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ color: uiTokens.colors.textPrimary }}>
                        {t('client_dashboard.how_it_works_body')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHowItWorksOpen(false)} sx={{ textTransform: 'none', color: uiTokens.colors.primary }}>
                        {t('recommended_meal.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
